import { IconButton, Stack, ThemeProvider, Typography } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { AddIcon } from '@/assets/icons';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { setupPropertyPanelTheme } from '@/modules/common/theme';
import { StyledTooltip } from '@modules/common/components/StyledTooltip';
import { loadCarrierTypeToLoadTypes } from '@/modules/floorplanDesign/helpers/setup';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useLoadCarrierStore } from '@/modules/loadCarriers/store/useLoadCarrierStore';
import { IsLatestRevisionSelector } from '@/selectors';

import { getNonShuttleIds, isShuttleLoadCarrierId } from '../../helpers/shuttleSelectionHelpers';
import { useDoesVehiclesHaveLoadTypeCheck } from '../../hooks/useDoesAllVehiclesHaveLoadTypeCheck';
import { useIsLoadTypeBeingUsedCheck } from '../../hooks/useIsLoadTypeBeingUsedCheck';
import { FloorPlanPropertyItem } from '../common/FloorPlanPropertyItem';
import { LoadCarrierTypeSelectDialog } from './LoadCarrierTypeSelectDialog';

const LoadCarrierTypesComponent = () => {
  const { t } = useTranslation(['interface']);
  const [open, setOpen] = useState(false);
  const isLatest = IsLatestRevisionSelector.useReactive();
  const { getLoadTypeById, loadTypesInStore, vehiclesInStore } = useFloorplanDesignStore(
    useShallow((state) => ({
      getLoadTypeById: state.getLoadTypeById,
      loadTypesInStore: state.setup.loadTypes,
      vehiclesInStore: state.setup.vehicleTypes,
    })),
  );
  const allLoadCarriers = useLoadCarrierStore((state) => state.allLoadCarriers);
  const { isLoadTypeBeingUsed } = useIsLoadTypeBeingUsedCheck();
  const { doesVehiclesHaveLoadType } = useDoesVehiclesHaveLoadTypeCheck();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const hasAreas = useFloorplanDesignStore((state) => state.hasAreas());
  const loadTypesInUse = useFloorplanDesignStore(
    useShallow((state) =>
      state.setup.loadTypes
        .filter(({ id }) => isLoadTypeBeingUsed(id))
        .map(({ id }) => id),
    ),
  );
  const enabledLoadTypeIds = useMemo(
    () => loadTypesInStore.map(({ id }) => id),
    [loadTypesInStore],
  );
  const enabledLoadCarriers = useMemo(
    () => allLoadCarriers.getByIds(enabledLoadTypeIds),
    [allLoadCarriers, enabledLoadTypeIds],
  );
  const enabledRegularLoadTypeCount = useMemo(
    () => getNonShuttleIds(enabledLoadTypeIds).length,
    [enabledLoadTypeIds],
  );
  const canAddLoadType = isLatest && !hasAreas;
  const addButtonTooltipText = hasAreas
    ? t('setup.floorPlan.load_carrier_types.add_button.disabled')
    : t('setup.floorPlan.load_carrier_types.add_button.title');
  const deleteButtonTooltipText = t('setup.floorPlan.load_carrier_types.delete_button.title');
  const loadTypeInUseTooltipText = t(
    'setup.floorPlan.load_carrier_types_select_dialog.warnings.load_type_in_use',
  );

  const onSubmit = useCallback(
    (ids: string[]) => {
      setOpen(false);

      const loadTypesToRemove = enabledLoadTypeIds.filter((id) => !ids.includes(id));
      const loadTypesToAdd = ids.filter((id) => !enabledLoadTypeIds.includes(id));

      fddmSvc.addLoadTypes(loadCarrierTypeToLoadTypes(allLoadCarriers.getByIds(loadTypesToAdd)));
      fddmSvc.deleteLoadTypes(
        loadCarrierTypeToLoadTypes(allLoadCarriers.getByIds(loadTypesToRemove)),
      );

      doesVehiclesHaveLoadType(
        vehiclesInStore.map((vehicle) => vehicle.id),
        ids,
      );
    },
    [enabledLoadTypeIds, fddmSvc, allLoadCarriers, doesVehiclesHaveLoadType, vehiclesInStore],
  );

  const onDeleteClick = useCallback(
    (id: string) => {
      if (!isLatest || isLoadTypeBeingUsed(id)) return;

      const loadTypeToDelete = getLoadTypeById(id);
      if (!loadTypeToDelete) return;

      fddmSvc.deleteLoadTypes([loadTypeToDelete]);
    },
    [fddmSvc, getLoadTypeById, isLatest, isLoadTypeBeingUsed],
  );

  const isLoadTypeDeletable = useCallback(
    (id: string): boolean => isShuttleLoadCarrierId(id) || enabledRegularLoadTypeCount > 1,
    [enabledRegularLoadTypeCount],
  );

  const onOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <ThemeProvider theme={setupPropertyPanelTheme}>
        <Stack spacing={1} sx={{ px: 2 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography
              align='center'
              sx={{
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              {t('setup.floorPlan.load_carrier_types.title', 'Load carrier type')}
            </Typography>
            <StyledTooltip title={isLatest ? addButtonTooltipText : ''}>
              <span>
                <IconButton
                  disabled={!canAddLoadType}
                  onClick={onOpen}
                  sx={{ padding: 0, border: 'none' }}
                >
                  <AddIcon data-testid='add-icon' />
                </IconButton>
              </span>
            </StyledTooltip>
          </Stack>
          <Stack gap={1}>
            {enabledLoadCarriers.map((item) => {
              const inUse = loadTypesInUse.includes(item.id);

              return (
                <FloorPlanPropertyItem
                  key={item.id}
                  name={item.name}
                  imageUrl={item.image.url}
                  disabled={!isLatest || inUse}
                  deleteBtnTooltipText={
                    inUse ? loadTypeInUseTooltipText : deleteButtonTooltipText
                  }
                  deletable={isLoadTypeDeletable(item.id)}
                  onDeleteClick={() => onDeleteClick(item.id)}
                />
              );
            })}
          </Stack>
        </Stack>
      </ThemeProvider>
      <LoadCarrierTypeSelectDialog
        open={open}
        enabledLoadCarriersIds={enabledLoadTypeIds}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </>
  );
};

export const LoadCarrierTypes = memo(LoadCarrierTypesComponent);